import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { UtilisateursJsonldUserworkgroupRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import {
    UtilisateurApiObject,
    UtilisateursGroupesTravailWithGroupLib,
} from '@europrocurement/l2d-domain';
import { SearchBar } from '../../atoms';
import { UtilisateurChoiceItem } from '../UtilisateurChoiceItem/UtilisateurChoiceItem';

export type UtilisateurSearchListProps = {
    users:
        | UtilisateursJsonldUserworkgroupRead[]
        | UtilisateurApiObject[]
        | UtilisateursGroupesTravailWithGroupLib[];
    selectedUser?: UtilisateursJsonldUserworkgroupRead | UtilisateurApiObject | null;
    setSelectedUser?: React.Dispatch<
        React.SetStateAction<UtilisateursJsonldUserworkgroupRead | UtilisateurApiObject | null>
    >;
    extraSearchBarButton?: JSX.Element;
    listTitle?: string;
    userInfo?: (
        user:
            | UtilisateursGroupesTravailWithGroupLib
            | UtilisateurApiObject
            | UtilisateursJsonldUserworkgroupRead,
    ) => string;
    onSearch?: (searchTerm: string) => void;
    isSearching?: boolean;
};
export const UtilisateurSearchList: React.FunctionComponent<UtilisateurSearchListProps> =
    function ({
        users,
        selectedUser,
        setSelectedUser,
        extraSearchBarButton,
        listTitle,
        userInfo,
        onSearch,
        isSearching,
    }) {
        const { palette } = useTheme();
        const [searchBarValue, setSearchBarValue] = useState('');
        const [usersFiltered, setUsersFiltered] = useState<UtilisateursJsonldUserworkgroupRead[]>(
            [],
        );
        const [lastSearchTerm, setLastSearchTerm] = useState('');
        const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

        // Initialiser usersFiltered au montage et quand users change (toujours mettre à jour)
        useEffect(() => {
            setUsersFiltered(users);
        }, [users]);

        // Debounced search effect
        useEffect(() => {
            if (!onSearch) {
                const searchValue = searchBarValue.trim().toUpperCase();
                let filteredUsers = users;
                if (searchValue) {
                    filteredUsers = users.filter(
                        (user) =>
                            user.nom?.toUpperCase().includes(searchValue) ||
                            user.prenom?.toUpperCase().includes(searchValue) ||
                            user.mail?.toUpperCase().includes(searchValue),
                    );
                }
                setUsersFiltered(filteredUsers);
                return undefined;
            }

            // Si on a modifié le texte de recherche, lancer une nouvelle recherche
            if (searchBarValue.trim() && searchBarValue.trim() !== lastSearchTerm) {
                if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                }

                searchTimeoutRef.current = setTimeout(() => {
                    setLastSearchTerm(searchBarValue.trim());
                    onSearch(searchBarValue.trim());
                }, 300);
            }

            // Si la barre de recherche est vide, réinitialiser
            if (!searchBarValue.trim()) {
                setLastSearchTerm('');
                setUsersFiltered(users);
            }

            return () => {
                if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                }
            };
        }, [users, searchBarValue, onSearch, lastSearchTerm]);

        const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;
            setSearchBarValue(value);
        };

        // Préparer la liste affichée : mettre le selectedUser en premier
        const selectedId = selectedUser?.id;
        const filteredWithoutSelected = selectedId
            ? usersFiltered.filter((u) => u.id !== selectedId)
            : usersFiltered;
        const listToDisplay = selectedUser
            ? [selectedUser, ...filteredWithoutSelected]
            : usersFiltered;

        return (
            <>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                    {extraSearchBarButton}
                    <SearchBar
                        placeholder="Rechercher un utilisateur par nom, prénom, mail..."
                        onChange={handleSearchChange}
                        value={searchBarValue}
                        sx={{ marginTop: 0 }}
                    />
                </Box>
                {listTitle && (
                    <Typography
                        variant="l2dbodyxs"
                        sx={{ color: 'text.gris70' }}
                    >
                        {listTitle}
                    </Typography>
                )}
                <Box
                    data-testid="applying-members-list-container"
                    sx={{ maxHeight: '200px', overflow: 'auto' }}
                >
                    {isSearching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Typography
                                variant="l2dbodyxs"
                                sx={{ color: 'text.gris70' }}
                            >
                                Recherche en cours...
                            </Typography>
                        </Box>
                    ) : (
                        listToDisplay.map((user) => (
                            <UtilisateurChoiceItem
                                key={user.id}
                                user={user}
                                setSelectedUser={setSelectedUser}
                                selectedUser={selectedUser}
                                userInfo={userInfo ? userInfo(user) : undefined}
                                sx={{
                                    ':hover': {
                                        backgroundColor: palette.grey.A700,
                                    },
                                }}
                            />
                        ))
                    )}
                </Box>
            </>
        );
    };
